import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion'; 
import { 
  MdPhoneIphone, MdCheckCircle, MdGroups, 
  MdBusinessCenter, MdStars, MdArrowForward,
  MdExpandMore, MdKeyboardArrowRight, MdEmail, MdChatBubbleOutline,
  MdClose 
} from 'react-icons/md';
import { auth, db } from "./firebase"; // Added db
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore"; // Added Firestore imports
import logo from "./logo.png"; 
import qrCodeImg from "./qrcode.png"; 
import heroPhones from "./hero-phones.jpg"; 
import featuresImg from "./features.png"; 
import personalImg from "./personal.png"; 
import Feedback from './feedback'; 

// Styles from AppHeader.js
const PrimaryColor = "#005b63"; 
const LightText = "#37474f";
const BorderColor = "#dadce0";
const BgColor = "#f1f3f4";
const AccentTeal = "#19a39a"; 

// --- UPDATED MODAL STYLES ---
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999; 
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: white;
  padding: 40px 20px 20px 20px;
  border-radius: 24px;
  position: relative;
  width: 90%;
  max-width: 600px; 
  min-height: 400px; 
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f1f3f4;
  border: none;
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #5f6368;
  z-index: 10001;
  transition: all 0.2s;
  &:hover { background: #e8eaed; color: #000; }
`;

// --- Header Component ---
const NavContainer = styled.nav`
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 40px; background: white; border-bottom: 1px solid #f1f1f1;
    height: 80px; position: sticky; top: 0; z-index: 2000;
    @media (max-width: 768px) { padding: 0 20px; }
`;

const LeftSection = styled.div`
    display: flex; align-items: center; gap: 12px; cursor: pointer;
    img { height: 40px; width: 40px; border-radius: 50%; }
    h1 { font-size: 18px; font-weight: 800; color: #1a202c; margin: 0; font-family: sans-serif; 
      @media (max-width: 480px) { display: none; } 
    }
`;

const NavLinks = styled.div`
    display: flex; gap: 20px;
    @media (max-width: 1024px) { display: none; }
    a, span { text-decoration: none; color: #4a5568; font-size: 15px; font-weight: 600; font-family: sans-serif; cursor: pointer; }
    span:hover { color: ${AccentTeal}; }
`;

const PremiumBadge = styled.div`
    display: flex; align-items: center; gap: 8px; background: #f0fdfa;
    border: 1px solid #ccfbf1; padding: 8px 16px; border-radius: 12px;
    @media (max-width: 768px) { display: none; } 
    .icon { color: ${AccentTeal}; font-size: 20px; }
    .text-box { display: flex; flex-direction: column; line-height: 1.2; }
    .title { font-size: 12px; font-weight: 800; color: #134e4a; }
    .sub { font-size: 10px; font-weight: 700; color: ${AccentTeal}; }
`;

const PageContainer = styled.div`
  font-family: sans-serif; color: ${LightText}; background-color: #ffffff;
  overflow-x: hidden;
`;

const HeroSection = styled.section`
  display: grid; grid-template-columns: 1fr 1fr; padding: 80px 60px;
  align-items: center; background: white; gap: 40px;
  @media (max-width: 968px) { 
    grid-template-columns: 1fr; 
    text-align: center; 
    padding: 60px 20px;
    display: flex;
    flex-direction: column-reverse; 
  }
`;

const MainHeading = styled.h1`
  font-size: 64px; font-weight: 850; line-height: 1.1; color: #1a1a1b; margin-bottom: 24px;
  span { color: ${AccentTeal}; }
  @media (max-width: 768px) { font-size: 40px; }
`;

const QRContainer = styled.div`
  margin-top: 40px; display: flex; align-items: center; gap: 20px;
  padding: 15px; border-radius: 20px; background: #f8fafc;
  width: fit-content; border: 1px solid #e2e8f0;
  @media (max-width: 968px) { margin: 40px auto 0 auto; }
`;

const FeaturesGrid = styled.div`
  padding: 80px 60px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  background: #f8fafc;
  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 768px) { grid-template-columns: 1fr; padding: 40px 20px; }
`;

const FeatureCard = styled(motion.div)`
  background: white;
  padding: 40px 30px;
  border-radius: 30px;
  border: 1px solid #eef2f6;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 91, 99, 0.08);
    border-color: ${AccentTeal};
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 18px;
  background: ${props => props.bg || '#e6f4f1'};
  color: ${props => props.color || AccentTeal};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin-bottom: 25px;
  @media (max-width: 768px) { margin: 0 auto 25px auto; }
`;

const PowerfulFeaturesContainer = styled.section`
  padding: 100px 60px;
  background: #ffffff;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 768px) { padding: 60px 20px; }
`;

const PowerfulHeader = styled.h2`
  font-size: 48px;
  font-weight: 850;
  color: #1a1a1b;
  margin-bottom: 60px;
  span { color: ${AccentTeal}; }
  @media (max-width: 768px) { font-size: 32px; text-align: center; }
`;

const FeaturesFlex = styled.div`
  display: flex;
  gap: 80px;
  align-items: flex-start;
  @media (max-width: 968px) { flex-direction: column; gap: 40px; }
`;

const AccordionList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  width: 100%;
`;

const AccordionItem = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 25px;
  border-radius: 15px;
  background: ${props => props.active ? AccentTeal : '#f8fafc'};
  color: ${props => props.active ? 'white' : '#1a1a1b'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  .num { font-size: 14px; font-weight: 700; margin-right: 15px; opacity: 0.8; }
  .title { font-size: 18px; font-weight: 700; flex: 1; }
`;

const MockupContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  width: 100%;
  img {
    max-width: 100%;
    height: auto;
    border-radius: 40px;
    box-shadow: 0 30px 60px rgba(0,0,0,0.1);
  }
`;

const ContentSection = styled.section`
  padding: 80px 60px;
  display: flex;
  flex-direction: column;
  gap: 120px;
  @media (max-width: 768px) { padding: 40px 20px; gap: 60px; }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
  flex-direction: ${props => props.reverse ? 'row-reverse' : 'row'};
  @media (max-width: 968px) { flex-direction: column; text-align: center; }
`;

const TextBox = styled.div`
  flex: 1;
  h2 { font-size: 44px; font-weight: 850; color: #1a1a1b; margin-bottom: 20px; 
    span { color: ${AccentTeal}; } 
    @media (max-width: 768px) { font-size: 32px; }
  }
  p { font-size: 18px; line-height: 1.6; color: #5f6368; margin-bottom: 30px; }
  button { @media (max-width: 768px) { margin: 0 auto; } }
`;

const ImageBox = styled.div`
  flex: 1;
  width: 100%;
  img { width: 100%; border-radius: 40px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); }
`;

const FooterContainer = styled.footer`
  padding: 80px 60px 40px 60px;
  background: #ffffff;
  border-top: 1px solid #f1f1f1;
  @media (max-width: 768px) { padding: 40px 20px 20px 20px; }
`;

const FooterTop = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 60px;
  @media (max-width: 768px) { 
    display: grid;
    grid-template-columns: 1fr 1fr; 
    gap: 30px;
    text-align: left;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  h4 { font-size: 16px; font-weight: 900; color: #1a202c; text-transform: uppercase; margin-bottom: 10px; }
  span { color: #5f6368; font-size: 15px; cursor: pointer; &:hover { color: ${AccentTeal}; } }
  
  @media (max-width: 768px) {
    &:last-child {
      grid-column: span 2;
      align-items: center;
      text-align: center;
      margin-top: 10px;
    }
  }
`;

const DownloadBtn = styled.button`
  background: #111827;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
`;

const ContactInfo = styled.div`
  display: flex;
  gap: 40px;
  padding-bottom: 40px;
  border-bottom: 1px solid #f1f1f1;
  @media (max-width: 768px) { flex-direction: column; align-items: center; gap: 20px; }
  .item { display: flex; align-items: center; gap: 8px; color: ${AccentTeal}; font-weight: 700; font-size: 16px; cursor: pointer; }
`;

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 30px;
  font-size: 13px;
  color: #718096;
  @media (max-width: 768px) { flex-direction: column; gap: 20px; text-align: center; }
  .links { display: flex; gap: 20px; font-weight: 700; color: #4a5568; span { cursor: pointer; } }
`;

const AvaamiConnectApp = () => {
  const [activeFeature, setActiveFeature] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false); 
  const [isLoading, setIsLoading] = useState(true); // Added loading state
  const navigate = useNavigate();

  // Redirect if user is logged in AND status is not "logged_out"
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            // Firestore-la status "logged_out"-nu irundha mattum intha page-la vaikurom
            // Oruvelai status "logged_in" illa vera ethuna kooda redirect panniduvom
            if (userData.status !== "logged_out") {
              navigate('/');
            }
          } else {
            // Profile doc illana puthu user, so home page-ku redirect
            navigate('/');
          }
        } catch (error) {
          console.error("Error fetching user status:", error);
        }
      }
      setIsLoading(false); // Finish checking
    });
    return () => unsubscribe();
  }, [navigate]);

  // Loading irukkumpothu onnum display pannatha maari empty return
  if (isLoading) return null;

  const powerfulFeatures = [
    "Personal Profile", "Dynamic Feed", "Corners", 
    "Business", "Secure Messaging", 
    "Job Board"
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7 } }
  };

  return (
    <PageContainer>
      <Feedback 
        isOpen={showFeedback} 
        onClose={() => setShowFeedback(false)} 
        userProfile={{fullName: "User"}} 
      />

      {/* Header */}
      <NavContainer>
        <LeftSection onClick={() => navigate('/')}>
            <img src={logo} alt="Logo" />
            <h1>Avaami Business Connect</h1>
        </LeftSection>
        <NavLinks>
            <a href="#feed">Feed</a>
            <span onClick={() => navigate('/businesscorners')}>Corners</span>
            <span onClick={() => navigate('/BusinessList')}>Business</span>
            <span onClick={() => navigate('/JobsPage')}>Jobs</span>
        </NavLinks>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <PremiumBadge>
                <MdStars className="icon" />
                <div className="text-box">
                    <span className="title">PREMIUM</span>
                    <span className="sub">TRY FOR ₹0</span>
                </div>
            </PremiumBadge>
            <span onClick={() => navigate('/register')} style={{ fontWeight: '700', color: AccentTeal, cursor: 'pointer', fontSize: '14px' }}>Register</span>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')} 
              style={{ backgroundColor: AccentTeal, color: 'white', border: 'none', padding: '8px 20px', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '14px' }}>
              Login
            </motion.button>
        </div>
      </NavContainer>

      {/* Hero Section */}
      <HeroSection>
        <motion.div initial="hidden" animate="visible" variants={fadeInLeft}>
          <MainHeading>Connect <br/> Collaborate <br/> <span>Grow Together</span></MainHeading>
          <p style={{fontSize: '18px', color: '#5f6368', marginBottom: '30px', maxWidth: '500px'}}>
            Our mission is to bridge the gap between businesses and talent by creating a secure digital space for professional collaboration.
          </p>
          <div style={{ display: 'flex', gap: '15px', justifyContent: window.innerWidth < 768 ? 'center' : 'flex-start' }}>
            <motion.button 
              whileHover={{ scale: 1.05 }}
              style={{ background: '#0a192f', color: 'white', border: 'none', padding: '14px 28px', borderRadius: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <MdPhoneIphone size={20}/> Get the App
            </motion.button>
          </div>
          <QRContainer>
             <img src={qrCodeImg} alt="QR Code" style={{ width: '80px', height: '80px', borderRadius: '12px' }} />
             <div style={{ textAlign: 'left' }}>
                <div style={{ color: AccentTeal, fontWeight: '800', fontSize: '13px', marginBottom: '4px' }}>SCAN TO DOWNLOAD ↓</div>
                <div style={{ fontSize: '11px', color: LightText, fontWeight: '600' }}>Available on iOS & Android</div>
             </div>
          </QRContainer>
        </motion.div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          style={{ width: '100%' }}>
            <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80" 
            style={{width: '100%', borderRadius: '30px', objectFit: 'cover'}} alt="Hero" />
        </motion.div>
      </HeroSection>

      {/* Features Grid */}
      <FeaturesGrid>
        {[
          { t: 'Business Ecosystem', d: 'Build a powerful network by connecting with local partners, verified vendors.', i: <MdGroups/>, bg: '#e0f2f1', color: '#00796b' },
          { t: 'Professional Pages', d: 'Launch your brand with dedicated pages that showcase your services.', i: <MdBusinessCenter/>, bg: '#fff3e0', color: '#ef6c00' },
          { t: 'Secure Messaging', d: 'Communicate with confidence using our end-to-end secure messaging system.', i: <MdCheckCircle/>, bg: '#f3e5f5', color: '#7b1fa2' }
        ].map((f, i) => (
          <FeatureCard 
            key={i}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            custom={i}>
            <IconWrapper bg={f.bg} color={f.color}>{f.i}</IconWrapper>
            <h3 style={{ fontSize: '22px', fontWeight: '800', marginBottom: '15px', color: '#1a1a1b', textAlign: window.innerWidth < 768 ? 'center' : 'left' }}>{f.t}</h3>
            <p style={{ color: '#5f6368', lineHeight: '1.6', fontSize: '16px', textAlign: window.innerWidth < 768 ? 'center' : 'left' }}>{f.d}</p>
          </FeatureCard>
        ))}
      </FeaturesGrid>

      {/* Powerful Features */}
      <PowerfulFeaturesContainer>
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
          <PowerfulHeader>Explore our <span>Powerful Features</span></PowerfulHeader>
        </motion.div>
        <FeaturesFlex>
          <AccordionList>
            {powerfulFeatures.map((feature, idx) => (
              <AccordionItem 
                key={idx} 
                active={activeFeature === idx} 
                onClick={() => setActiveFeature(idx)}
                whileHover={{ x: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="num">0{idx + 1}</span>
                  <span className="title">{feature}</span>
                </div>
                {activeFeature === idx ? <MdKeyboardArrowRight size={24}/> : <MdExpandMore size={24}/>}
              </AccordionItem>
            ))}
          </AccordionList>
          <MockupContainer>
            <motion.img 
              key={activeFeature}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              src={personalImg} 
              alt="App Preview" />
          </MockupContainer>
        </FeaturesFlex>
      </PowerfulFeaturesContainer>

      {/* Content Section */}
      <ContentSection>
        <Row>
          <TextBox>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInLeft}>
              <h2>Build Your <span>Business Identity</span></h2>
              <p>Professional business pages designed to showcase your brand. Connect with local suppliers and grow your network.</p>
              <button onClick={() => navigate('/businesscorners')} style={{ background: 'none', color: AccentTeal, border: 'none', fontWeight: '800', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                Learn About Corners <MdArrowForward size={22}/>
              </button>
            </motion.div>
          </TextBox>
          <ImageBox>
            <motion.img 
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              src={heroPhones} alt="Business Showcase" />
          </ImageBox>
        </Row>
      </ContentSection>

      {/* Footer */}
      <FooterContainer>
        <FooterTop>
          <FooterColumn>
            <h4>Pages</h4>
            <span>Feed</span>
            <span>Corners</span>
            <span>Business</span>
            <span>Jobs</span>
          </FooterColumn>
          <FooterColumn>
            <h4>Company</h4>
            <span>About Avaami Business Connect</span>
            <span>Privacy Policy</span>
            <span>User Guideline</span>
          </FooterColumn>
          <FooterColumn>
            <h4>Get Started</h4>
            <motion.div whileHover={{ scale: 1.05 }}>
              <DownloadBtn>
                <MdPhoneIphone size={20}/> Download App
              </DownloadBtn>
            </motion.div>
          </FooterColumn>
        </FooterTop>

        <ContactInfo>
          <div className="item"><MdEmail size={20}/> support@avaami.com</div>
          <motion.div 
            whileHover={{ scale: 1.1, color: PrimaryColor }}
            className="item" 
            onClick={() => setShowFeedback(true)} 
            style={{ transition: 'none' }}>
            <MdChatBubbleOutline size={20}/> Submit Feedback
          </motion.div>
        </ContactInfo>

        <FooterBottom>
          <div className="links">
            <span>Terms of Service</span>
            <span>Privacy Policy</span>
          </div>
          <div style={{ marginTop: '10px' }}>© 2026, Avaami Technologies Pvt. Ltd.</div>
        </FooterBottom>
      </FooterContainer>
    </PageContainer>
  );
};

export default AvaamiConnectApp;